/**
 * User message row (ticket 3525): moved from forge's MessagesTimeline.tsx
 * unchanged (attachments, CMMD context card, copy, revert). No upstream
 * pin drift; forge's user-bubble presentation predates the port.
 *
 * @module TimelineUserMessageRow
 */

import { Button } from "../ui/button";
import { ChevronDownIcon, ChevronRightIcon, Undo2Icon } from "lucide-react";
import { use, useState } from "react";
import { formatTimestamp } from "../../timestampFormat";
import type { ChatFileAttachment, ChatImageAttachment } from "../../types";
import { buildExpandedImagePreview } from "./ExpandedImagePreview";
import { FileAttachmentLink } from "./FileAttachmentLink";
import { ImageAttachmentDownload } from "./ImageAttachmentDownload";
import { UserMessageBody } from "./UserMessageBody";
import { MessageCopyButton } from "./MessageCopyButton";
import {
  deriveDisplayedUserMessageState,
  type ParsedCmmdContextEntry,
} from "~/lib/terminalContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { TimelineRowCtx } from "./TimelineRowContext";
import type { MessagesTimelineRow } from "./MessagesTimeline.rowTypes";

/** The user turn: attachments, CMMD context card, body, copy, and revert. */
export function TimelineUserMessageRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "message" }>;
}) {
  const ctx = use(TimelineRowCtx);
  const userAttachments = row.message.attachments ?? [];
  const userImages = userAttachments.filter(
    (attachment): attachment is ChatImageAttachment => attachment.type === "image",
  );
  const userFiles = userAttachments.filter(
    (attachment): attachment is ChatFileAttachment => attachment.type === "file",
  );
  const displayedUserMessage = deriveDisplayedUserMessageState(row.message.text);
  const terminalContexts = displayedUserMessage.contexts;
  const cmmdContext = displayedUserMessage.cmmdContext;
  const canRevertAgentWork = typeof row.revertTurnCount === "number";
  return (
    <div className="flex justify-end">
      <div className="group relative max-w-[80%] rounded-2xl rounded-br-sm border border-border bg-secondary px-4 py-3">
        {userImages.length > 0 ? (
          <div className="mb-2 grid max-w-[420px] grid-cols-2 gap-2">
            {userImages.map((image: ChatImageAttachment) => (
              <div
                key={image.id}
                className="overflow-hidden rounded-lg border border-border/80 bg-background/70"
              >
                {image.previewUrl ? (
                  <button
                    type="button"
                    className="h-full w-full cursor-zoom-in"
                    aria-label={`Preview ${image.name}`}
                    onClick={() => {
                      const preview = buildExpandedImagePreview(userImages, image.id);
                      if (!preview) return;
                      ctx.onImageExpand(preview);
                    }}
                  >
                    <img
                      src={image.previewUrl}
                      alt={image.name}
                      className="block h-auto max-h-[220px] w-full object-cover"
                    />
                  </button>
                ) : (
                  <ImageAttachmentDownload
                    environmentId={ctx.activeThreadEnvironmentId}
                    image={image}
                  />
                )}
              </div>
            ))}
          </div>
        ) : null}
        {userFiles.length > 0 ? (
          <div className="mb-2 flex max-w-[420px] flex-col gap-1.5">
            {userFiles.map((file) => (
              <FileAttachmentLink
                key={file.id}
                environmentId={ctx.activeThreadEnvironmentId}
                file={file}
              />
            ))}
          </div>
        ) : null}
        {cmmdContext ? <UserMessageCmmdContextCard context={cmmdContext} /> : null}
        {displayedUserMessage.visibleText.trim().length > 0 || terminalContexts.length > 0 ? (
          <UserMessageBody
            text={displayedUserMessage.visibleText}
            terminalContexts={terminalContexts}
          />
        ) : null}
        <div className="mt-1.5 flex items-center justify-end gap-2">
          <div className="flex items-center gap-1.5 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100">
            {displayedUserMessage.copyText ? (
              <MessageCopyButton text={displayedUserMessage.copyText} />
            ) : null}
            {canRevertAgentWork ? (
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={ctx.isRevertingCheckpoint || ctx.isWorking}
                onClick={() => ctx.onRevertUserMessage(row.message.id)}
                title="Revert to this message"
              >
                <Undo2Icon className="size-3" />
              </Button>
            ) : null}
          </div>
          <p className="text-right text-xs text-muted-foreground/50">
            {formatTimestamp(row.message.createdAt, ctx.timestampFormat)}
          </p>
        </div>
      </div>
    </div>
  );
}

function UserMessageCmmdContextCard(props: { context: ParsedCmmdContextEntry }) {
  const [open, setOpen] = useState(false);
  const previewLine =
    props.context.body
      .split("\n")
      .map((line) => line.trim())
      .find((line) => line.length > 0) ?? "Context attached";

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="mb-2 overflow-hidden rounded-md border border-border/55 bg-background/55">
        <div className="flex w-full items-center gap-1 pr-1.5 text-xs text-foreground/85">
          <CollapsibleTrigger
            className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-2 text-left hover:bg-background/55"
            aria-label={open ? "Hide task context" : "Show task context"}
          >
            {open ? (
              <ChevronDownIcon className="size-3.5 text-muted-foreground/80" />
            ) : (
              <ChevronRightIcon className="size-3.5 text-muted-foreground/80" />
            )}
            <span className="shrink-0 font-medium tracking-[0.02em]">Task context</span>
            {!open ? (
              <span className="min-w-0 truncate text-muted-foreground/85">{previewLine}</span>
            ) : null}
          </CollapsibleTrigger>
          <MessageCopyButton
            text={props.context.body}
            size="icon-xs"
            variant="ghost"
            ariaLabel="Copy task context"
            className="shrink-0"
          />
        </div>
        <CollapsibleContent>
          <div className="border-border/50 border-t px-2.5 py-2">
            <pre className="whitespace-pre-wrap wrap-break-word text-sm leading-relaxed text-foreground/85">
              {props.context.body}
            </pre>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
